import type { IconMapping } from "@foodpilot/foods";

import type { TransportCircuit } from "src/api/client-api/transport/useTransportCircuits";
import type { TransportLeg } from "src/api/client-api/transport/useTransportLegs";
import type { TransportModeChoice } from "src/api/sure-api/useSearchTransportModes";

export type TransportModeIcon = {
    key: string;
    icon: keyof IconMapping | "flightTakeoff";
    label: string;
};

const FALLBACK_TRANSPORT_ICON: keyof IconMapping = "transportation";

const iconForTransportMode = (mode: TransportModeChoice | undefined): TransportModeIcon["icon"] => {
    if (!mode) {
        return FALLBACK_TRANSPORT_ICON;
    }

    const code = mode.code.toLowerCase();
    if (code.includes("train") || code.includes("rail")) {
        return "train";
    }

    switch (mode.family) {
        case "sea":
            return mode.isRefrigerated ? "refrigeratedBoat" : "boat";
        case "land":
            return mode.isRefrigerated ? "refrigeratedTransport" : "transportation";
        case "air":
            return "flightTakeoff";
        default:
            return FALLBACK_TRANSPORT_ICON;
    }
};

export const getTransportLegModeIcons = (
    leg: Pick<TransportLeg, "transportModeId">,
    transportModes: TransportModeChoice[],
): TransportModeIcon[] => {
    const mode =
        leg.transportModeId != null ? transportModes.find((item) => item.id === leg.transportModeId) : undefined;

    return [
        {
            key: mode ? `mode-${mode.id}` : "mode-unknown",
            icon: iconForTransportMode(mode),
            label: mode?.label ?? "Transport mode",
        },
    ];
};

export const getTransportCircuitModeIcons = (
    circuit: Pick<TransportCircuit, "circuitLegs">,
    transportModes: TransportModeChoice[],
): TransportModeIcon[] => {
    const icons: TransportModeIcon[] = [];
    const seenKeys = new Set<string>();

    for (const circuitLeg of [...(circuit.circuitLegs ?? [])].sort((a, b) => a.orderIndex - b.orderIndex)) {
        const leg = circuitLeg.transportLeg;
        if (typeof leg !== "object" || leg === null || !("transportModeId" in leg)) {
            continue;
        }

        const mode =
            leg.transportModeId != null ? transportModes.find((item) => item.id === leg.transportModeId) : undefined;
        const key = mode ? `mode-${mode.id}` : `mode-unknown-${icons.length}`;

        if (seenKeys.has(key)) {
            continue;
        }

        seenKeys.add(key);
        icons.push({
            key,
            icon: iconForTransportMode(mode),
            label: mode?.label ?? "Transport mode",
        });
    }

    return icons.length > 0 ?
            icons
        :   [
                {
                    key: "mode-unknown",
                    icon: FALLBACK_TRANSPORT_ICON,
                    label: "Transport mode",
                },
            ];
};
